import { useEffect, useState, useCallback } from "react";
import { AlertTriangle } from "lucide-react";
import adminApi from "@/lib/adminApi";
import {
  PageHeader, AdminCard, StatCard, DataTable, StatusPill, SearchInput, FilterChips,
  PageLoader, datetimeFromIso,
} from "@/admin/components";

const FILTERS = ["All", "SMS", "Email", "Failed Only"];

export default function Notifications() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    const { data } = await adminApi.get("/admin/notifications", { params: { search, filter, days: 7 } });
    setData(data);
  }, [search, filter]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    { key: "created_at", label: "Timestamp", render: (r) => datetimeFromIso(r.created_at) },
    { key: "trigger_type", label: "Trigger", render: (r) => <span className="text-[13px]">{r.trigger_type}</span> },
    { key: "recipient_type", label: "Recipient", render: (r) => <span className="capitalize">{r.recipient_type}</span> },
    { key: "recipient_name", label: "Name", render: (r) => r.recipient_name || "—" },
    { key: "recipient_contact", label: "Contact", render: (r) => r.recipient_contact },
    { key: "channel", label: "Channel", render: (r) => <StatusPill tone={r.channel === "sms" ? "amber" : "gray"} label={r.channel.toUpperCase()} /> },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
    { key: "error_message", label: "Error", render: (r) => r.error_message ? <span className="text-[12px] text-[#c0392b]">{r.error_message.slice(0, 40)}</span> : "" },
  ];

  const rows = (data?.notifications || []).map((r) => ({ ...r, _accent: r.status === "failed" }));

  return (
    <div data-testid="admin-notifications-page">
      <PageHeader title="Notifications" subtitle="Delivery log · last 7 days" />

      {data && (
        <div className="mb-6 grid grid-cols-3 gap-4">
          <StatCard label="SMS delivery · 24h" value={`${data.stats.sms_rate}%`} />
          <StatCard label="Email delivery · 24h" value={`${data.stats.email_rate}%`} />
          <StatCard label="Failed · 24h" value={data.stats.failed} />
        </div>
      )}

      {data && data.stats.sms_rate < 90 && (
        <div className="mb-5 flex items-center gap-2 rounded-[12px] bg-[#fdf3e3] px-4 py-3 text-[13px] font-medium text-[#9a6a16]">
          <AlertTriangle size={16} /> SMS delivery rate is {data.stats.sms_rate}%. Check MSG91 account status.
        </div>
      )}

      <div className="mb-5 flex flex-col gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by recipient name, mobile, or email..." testid="notif-search" />
        <FilterChips options={FILTERS} value={filter} onChange={setFilter} testidPrefix="notif-filter" />
      </div>

      <AdminCard className="overflow-hidden">
        {data ? <DataTable columns={columns} rows={rows} testid="notifications-table" empty="No notifications in this period." /> : <PageLoader />}
      </AdminCard>
    </div>
  );
}
